import { z } from "zod/v4"
import { DocumentStatus } from "../common"
import type { DocumentSnapshotLike } from "../firestore"
import { StampMissionId } from "../id"
import { CreatedAt, UpdatedAt } from "./system"

const MissionTimeScopeRetrospective = z.literal("retrospective").describe("ミッション公開前を含めて条件達成した場合完了になる")
const MissionTimeScopeSinceMissionActivated = z.literal("since_mission_activated").describe("ミッションが公開されてから条件達成した場合に完了になる")

export const MissionTimeScope = z.enum([
  MissionTimeScopeRetrospective.value,
  MissionTimeScopeSinceMissionActivated.value,
]).describe("ミッション達成対象判定の期間の範囲")

export type MissionTimeScope = z.infer<typeof MissionTimeScope>

export const StampMissionProgressKindBoolean = z
  .literal("boolean")
  .describe(
    "進捗の種類。達成済み/未達成のみ"
  )
export const StampMissionProgressKindCounter = z
  .literal("counter")
  .describe(
    "進捗の種類。回数をカウントしtarget_count到達で達成"
  )

export const StampMissionProgressKind = z.enum([
  StampMissionProgressKindBoolean.value,
  StampMissionProgressKindCounter.value
]).describe(
  "進捗の種類。'boolean'＝達成済み/未達成のみ、'counter'＝回数をカウントしtarget_count到達で達成"
)
export type StampMissionProgressKind = z.infer<typeof StampMissionProgressKind>

export const StampMissionCompleteStatusInProgress = z.literal("in-progress").describe("未達成")
export const StampMissionCompleteStatusCompleted = z.literal("completed").describe("通常達成")
export const StampMissionCompleteStatusForceCompleted = z.literal("force-completed").describe("管理者による強制達成")

export const StampMissionCompleteStatus = z.enum([
  StampMissionCompleteStatusInProgress.value,
  StampMissionCompleteStatusCompleted.value,
  StampMissionCompleteStatusForceCompleted.value,
]).describe("ミッション達成ステータス")

export type StampMissionCompleteStatus = z.infer<typeof StampMissionCompleteStatus>

/** complete_status が達成状態（completed または force-completed）かどうかを判定する */
export function isStampMissionCompleted(completeStatus: StampMissionCompleteStatus | undefined): boolean {
  return completeStatus === "completed" || completeStatus === "force-completed"
}

/** completed: boolean から complete_status への変換（後方互換用） */
export function completeStatusFromLegacyCompleted(completed: boolean): StampMissionCompleteStatus {
  return completed ? "completed" : "in-progress"
}

export const MissionRepeatKindDaily = z
  .literal("daily")
  .describe("デイリーミッション")

export const MissionRepeatKindLimited = z
  .literal("limited")
  .describe("限定ミッション")

/**
 * ミッションの繰り返し種別
 */
export const MissionRepeatKind = z.enum([
  MissionRepeatKindDaily.value,
  MissionRepeatKindLimited.value,
]).describe("ミッションの繰り返し種別")

export type MissionRepeatKind = z.infer<typeof MissionRepeatKind>

export const MissionKind = z.string().describe("ミッション種別を示す文字列。")

// ========================================
// デイリーミッション
// ========================================

export const MissionKindLoginInTimeRange = z
  .literal("login_in_time_range")
  .describe("ミッション種別：指定時間帯にログイン（daily、現在から未来の行動のみカウント）")

export const MissionKindJoinSpace = z
  .literal("join_space")
  .describe("ミッション種別：友だちと会話しよう - 指定人数以上のメンバーが所属するスペースで通話に参加（daily、現在から未来の行動のみカウント、LIVEの視聴ユーザーはカウント対象外）")

export const MissionKindWatchLiveMinutes = z
  .literal("watch_live_minutes")
  .describe("ミッション種別：配信中のLIVEを累計N分視聴（daily、現在から未来の行動のみカウント、OFF LIVEは対象外）")

// ========================================
// 限定ミッション
// ========================================

export const MissionKindLoginDays = z
  .literal("login_days")
  .describe("ミッション種別：〇日ログインしよう - 累計ログイン数（limited、現在から未来の行動のみカウント）")

export const MissionKindEditUserIcon = z
  .literal("edit_user_icon")
  .describe("ミッション種別：プロフィール編集からユーザーアイコンを変更した（limited、過去の行動を含めてカウント）")

export const MissionKindSetSuperCallUser = z
  .literal("set_super_call_user")
  .describe("ミッション種別：ユーザーを選んでスーパーコールを受ける設定をする（limited、過去の行動を含めてカウント）")

export const MissionKindFollowUsers = z
  .literal("follow_users")
  .describe("ミッション種別：〇人フォローした（limited、過去の行動を含めてカウント）")

export const MissionKindSnsLink = z
  .literal("sns_link")
  .describe("ミッション種別：SNS連携をした（limited、過去の行動を含めてカウント）")

export const MissionKindShareFriendLink = z
  .literal("share_friend_link")
  .describe("ミッション種別：友達リンクをシェアした（limited、過去の行動を含めてカウント）")

export const MissionKindTalkMinutesWithNUsers = z
  .literal("talk_minutes_with_n_users")
  .describe("ミッション種別：n人以上で累計◯分会話した（limited、過去の行動を含めてカウント）")

type MissionKindListValue = {
  kind: MissionRepeatKind,
  value: string,
  label: string,
  progressKind: StampMissionProgressKind,
}

export const allMissionKinds: MissionKindListValue[] = [
  {
    kind: MissionRepeatKindDaily.value,
    value: MissionKindLoginInTimeRange.value,
    label: "指定時間帯にログイン",
    progressKind: StampMissionProgressKindBoolean.value,
  },
  {
    kind: MissionRepeatKindDaily.value,
    value: "join_space" as const,
    label: "友だちと会話（スペース参加）",
    progressKind: StampMissionProgressKindBoolean.value,
  },
  {
    kind: MissionRepeatKindDaily.value,
    value: "watch_live_minutes" as const,
    label: "LIVE視聴",
    progressKind: StampMissionProgressKindCounter.value,
  },
  {
    kind: MissionRepeatKindLimited.value,
    value: "login_days" as const,
    label: "累計ログイン日数",
    progressKind: StampMissionProgressKindCounter.value,
  },
  {
    kind: MissionRepeatKindLimited.value,
    value: "edit_user_icon" as const,
    label: "ユーザーアイコン変更",
    progressKind: StampMissionProgressKindBoolean.value,
  },
  {
    kind: MissionRepeatKindLimited.value,
    value: "set_super_call_user" as const,
    label: "スーパーコール受信設定",
    progressKind: StampMissionProgressKindBoolean.value,
  },
  {
    kind: MissionRepeatKindLimited.value,
    value: "follow_users" as const,
    label: "フォロー人数",
    progressKind: StampMissionProgressKindCounter.value,
  },
  {
    kind: MissionRepeatKindLimited.value,
    value: "sns_link" as const,
    label: "SNS連携",
    progressKind: StampMissionProgressKindBoolean.value,
  },
  {
    kind: MissionRepeatKindLimited.value,
    value: "share_friend_link" as const,
    label: "友達リンクシェア",
    progressKind: StampMissionProgressKindBoolean.value,
  },
  {
    kind: MissionRepeatKindLimited.value,
    value: "talk_minutes_with_n_users" as const,
    label: "n人以上で会話",
    progressKind: StampMissionProgressKindCounter.value,
  },
] as const

const dailyKinds = [
  MissionKindLoginInTimeRange.value,
  MissionKindJoinSpace.value,
  MissionKindWatchLiveMinutes.value,
] as const

const limitedKinds = [
  MissionKindLoginDays.value,
  MissionKindEditUserIcon.value,
  MissionKindSetSuperCallUser.value,
  MissionKindFollowUsers.value,
  MissionKindSnsLink.value,
  MissionKindShareFriendLink.value,
  MissionKindTalkMinutesWithNUsers.value,
] as const

export const StampMissionKindDaily = z.enum(dailyKinds).describe("デイリーミッション種別。日付ごとに進捗がリセットされます")

export const StampMissionKindLimited = z.enum(limitedKinds).describe("限定ミッション種別。1回のみ達成可能です")

export const StampMissionKindProgressKindCounter = z.union([
  MissionKindWatchLiveMinutes,
  MissionKindLoginDays,
  MissionKindFollowUsers,
  MissionKindTalkMinutesWithNUsers,
])

export type StampMissionKindProgressKindCounter = z.infer<typeof StampMissionKindProgressKindCounter>

export const StampMissionKindProgressKindBoolean = z.union([
  MissionKindLoginInTimeRange,
  MissionKindJoinSpace,
  MissionKindEditUserIcon,
  MissionKindSetSuperCallUser,
  MissionKindSnsLink,
  MissionKindShareFriendLink,
])

export type StampMissionKindProgressKindBoolean = z.infer<typeof StampMissionKindProgressKindBoolean>

export const StampMissionKind = z
  .enum([...dailyKinds, ...limitedKinds])
  .describe(
    "ミッション種別。具体的なミッションタイプを表します。各ミッション種別から、繰り返し動作（daily/limited）や過去の行動をカウントするかどうかが導かれます。詳細は https://www.notion.so/popopo-inc/20251127_-2b85e03d57e280a9b1ffc785441dd1dd を参照してください"
  )
export type StampMissionKind = z.infer<typeof StampMissionKind>
export type StampMissionKindDaily = z.infer<typeof StampMissionKindDaily>
export type StampMissionKindLimited = z.infer<typeof StampMissionKindLimited>

export const TimeWindow = z.object({
  start: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "HH:mm形式で入力してください").describe("開始時刻（JST、HH:mm形式）"),
  end: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "HH:mm形式で入力してください").describe("終了時刻（JST、HH:mm形式）"),
}).describe("時間帯の範囲")

export type TimeWindow = z.infer<typeof TimeWindow>

export const LoginInTimeRangeFromNowParams = z.object({
  time_window: TimeWindow.describe("ログイン可能な時間帯"),
})

export type LoginInTimeRangeFromNowParams = z.infer<typeof LoginInTimeRangeFromNowParams>

export const JoinSpaceFromNowParams = z.object({
  min_users: z.number().min(1).describe("スペース参加時に必要な最小人数（N人以上）"),
})
export type JoinSpaceFromNowParams = z.infer<typeof JoinSpaceFromNowParams>

export const WatchLiveMinutesFromNowParams = z.object({
  target_minutes: z.number().min(1).describe("目標視聴分数"),
})
export type WatchLiveMinutesFromNowParams = z.infer<typeof WatchLiveMinutesFromNowParams>

export const LoginDaysFromNowParams = z.object({
  target_days: z.number().min(1).describe("目標ログイン日数"),
})
export type LoginDaysFromNowParams = z.infer<typeof LoginDaysFromNowParams>

export const EditUserIconIncludePastParams = z.object({})
export type EditUserIconIncludePastParams = z.infer<typeof EditUserIconIncludePastParams>

export const SetSuperCallUserIncludePastParams = z.object({})
export type SetSuperCallUserIncludePastParams = z.infer<typeof SetSuperCallUserIncludePastParams>

export const FollowUsersIncludePastParams = z.object({
  target_users: z.number().min(1).describe("目標フォロー人数"),
})
export type FollowUsersIncludePastParams = z.infer<typeof FollowUsersIncludePastParams>

export const SnsLinkIncludePastParams = z.object({})
export type SnsLinkIncludePastParams = z.infer<typeof SnsLinkIncludePastParams>

export const ShareFriendLinkIncludePastParams = z.object({})
export type ShareFriendLinkIncludePastParams = z.infer<typeof ShareFriendLinkIncludePastParams>

export const TalkMinutesWithNUsersIncludePastParams = z.object({
  min_users: z.number().min(1).describe("会話に参加しているユーザーの最小人数（N人以上）"),
  target_minutes: z.number().min(1).describe("目標会話分数"),
})
export type TalkMinutesWithNUsersIncludePastParams = z.infer<typeof TalkMinutesWithNUsersIncludePastParams>

const StampMissionBase = z.object({
  kind: MissionKind,
  repeat_kind: MissionRepeatKind.describe("ミッションの繰り返し種別（daily / limited、MissionKind から非正規化）"),
  card_id: z.string().describe("親ドキュメントIDの冗長保持（コレクショングループクエリ対応のため。パスのIDはクエリ条件に使用できない制約がある）"),
  name: z.string().describe("ミッションの表示名"),
  description: z.string().optional().describe("ミッションの説明文"),
  award_stamps: z.number().describe("達成時に付与されるスタンプ数"),
  priority: z.number().describe("表示順。数値が大きいほど先に表示されます"),
  status: DocumentStatus.describe("ミッションの公開状態"),
})

// ========================================
// ミッションドキュメント定義
// ========================================

// デイリーミッション
const LoginInTimeRangeFromNowMission = z.object({
  kind: MissionKindLoginInTimeRange,
  params: LoginInTimeRangeFromNowParams.describe("時間帯パラメータ"),
})

const JoinSpaceFromNowMission = z.object({
  kind: MissionKindJoinSpace,
  params: JoinSpaceFromNowParams.describe("パラメータ（なし）"),
})

const WatchLiveMinutesFromNowMission = z.object({
  kind: MissionKindWatchLiveMinutes,
  params: WatchLiveMinutesFromNowParams.describe("視聴分数パラメータ"),
})

// 限定ミッション
const LoginDaysFromNowMission = z.object({
  kind: MissionKindLoginDays,
  params: LoginDaysFromNowParams.describe("ログイン日数パラメータ"),
  time_scope: MissionTimeScope.describe("ミッション達成対象判定の期間の範囲"),
})

const EditUserIconIncludePastMission = z.object({
  kind: MissionKindEditUserIcon,
  params: EditUserIconIncludePastParams.describe("パラメータ（なし）"),
  time_scope: MissionTimeScope.describe("ミッション達成対象判定の期間の範囲"),
})

const SetSuperCallUserIncludePastMission = z.object({
  kind: MissionKindSetSuperCallUser,
  params: SetSuperCallUserIncludePastParams.describe("パラメータ（なし）"),
  time_scope: MissionTimeScope.describe("ミッション達成対象判定の期間の範囲"),
})

const FollowUsersIncludePastMission = z.object({
  kind: MissionKindFollowUsers,
  params: FollowUsersIncludePastParams.describe("フォロー人数パラメータ"),
  time_scope: MissionTimeScope.describe("ミッション達成対象判定の期間の範囲"),
})

const SnsLinkIncludePastMission = z.object({
  kind: MissionKindSnsLink,
  params: SnsLinkIncludePastParams.describe("パラメータ（なし）"),
  time_scope: MissionTimeScope.describe("ミッション達成対象判定の期間の範囲"),
})

const ShareFriendLinkIncludePastMission = z.object({
  kind: MissionKindShareFriendLink,
  params: ShareFriendLinkIncludePastParams.describe("パラメータ（なし）"),
  time_scope: MissionTimeScope.describe("ミッション達成対象判定の期間の範囲"),
})

const TalkMinutesWithNUsersIncludePastMission = z.object({
  kind: MissionKindTalkMinutesWithNUsers,
  params: TalkMinutesWithNUsersIncludePastParams.describe("ユーザー数と会話分数パラメータ"),
  time_scope: MissionTimeScopeSinceMissionActivated.describe("ミッション達成対象判定の期間の範囲（since_mission_activated のみ）"),
})

export const StampMissionDocumentMeta = z.intersection(StampMissionBase, z.discriminatedUnion("kind", [
  // デイリーミッション
  LoginInTimeRangeFromNowMission,
  JoinSpaceFromNowMission,
  WatchLiveMinutesFromNowMission,
  // 限定ミッション
  LoginDaysFromNowMission,
  EditUserIconIncludePastMission,
  SetSuperCallUserIncludePastMission,
  FollowUsersIncludePastMission,
  SnsLinkIncludePastMission,
  ShareFriendLinkIncludePastMission,
  TalkMinutesWithNUsersIncludePastMission,
]))

export type StampMissionDocumentMeta = z.infer<typeof StampMissionDocumentMeta>

export const StampMissionDocumentBody = z.intersection(
  StampMissionDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type StampMissionDocumentBody = z.infer<typeof StampMissionDocumentBody>

export type StampMissionDocument = StampMissionDocumentBody & {
  id: StampMissionId,
}

export type StampMissionCounterDocument = Extract<StampMissionDocument, { kind: StampMissionKindProgressKindCounter }>
export type StampMissionBooleanDocument = Extract<StampMissionDocument, {kind: StampMissionKindProgressKindBoolean }>

const TimeWindowRequest = z.object({
  start: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "HH:mm形式で入力してください").describe("開始時刻（JST、HH:mm形式）"),
  end: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "HH:mm形式で入力してください").describe("終了時刻（JST、HH:mm形式）"),
})

const LoginInTimeRangeFromNowParamsRequest = z.object({
  timeWindow: TimeWindowRequest.describe("ログイン可能な時間帯"),
})

const JoinSpaceFromNowParamsRequest = z.object({
  minUsers: z.number().min(1).describe("スペース参加時に必要な最小人数（N人以上）"),
})

const WatchLiveMinutesFromNowParamsRequest = z.object({
  targetMinutes: z.number().min(1).describe("目標視聴分数"),
})

const LoginDaysFromNowParamsRequest = z.object({
  targetDays: z.number().min(1).describe("目標ログイン日数"),
})

const EditUserIconIncludePastParamsRequest = z.object({})

const SetSuperCallUserIncludePastParamsRequest = z.object({})

const FollowUsersIncludePastParamsRequest = z.object({
  targetUsers: z.number().min(1).describe("目標フォロー人数"),
})

const SnsLinkIncludePastParamsRequest = z.object({})

const ShareFriendLinkIncludePastParamsRequest = z.object({})

const TalkMinutesWithNUsersIncludePastParamsRequest = z.object({
  minUsers: z.number().min(1).describe("会話に参加しているユーザーの最小人数（N人以上）"),
  targetMinutes: z.number().min(1).describe("目標会話分数"),
})

const StampMissionBaseFieldsRequest = z.object({
  name: z.string().describe("ミッションの表示名"),
  description: z.string().optional().describe("ミッションの説明文"),
  awardStamps: z.number().describe("達成時に付与されるスタンプ数"),
  priority: z.number().describe("表示順。数値が大きいほど先に表示されます"),
  status: DocumentStatus.describe("ミッションの公開状態"),
})

// ========================================
// Create リクエスト定義
// ========================================

// デイリーミッション
const CreateLoginInTimeRangeFromNowMissionRequest = StampMissionBaseFieldsRequest.extend({
  kind: MissionKindLoginInTimeRange,
  params: LoginInTimeRangeFromNowParamsRequest.describe("時間帯パラメータ"),
})

const CreateJoinSpaceFromNowMissionRequest = StampMissionBaseFieldsRequest.extend({
  kind: MissionKindJoinSpace,
  params: JoinSpaceFromNowParamsRequest.describe("パラメータ（なし）"),
})

const CreateWatchLiveMinutesFromNowMissionRequest = StampMissionBaseFieldsRequest.extend({
  kind: MissionKindWatchLiveMinutes,
  params: WatchLiveMinutesFromNowParamsRequest.describe("視聴分数パラメータ"),
})

// 限定ミッション
const CreateLoginDaysFromNowMissionRequest = StampMissionBaseFieldsRequest.extend({
  kind: MissionKindLoginDays,
  params: LoginDaysFromNowParamsRequest.describe("ログイン日数パラメータ"),
  timeScope: MissionTimeScope.describe("ミッション達成対象判定の期間の範囲"),
})

const CreateEditUserIconIncludePastMissionRequest = StampMissionBaseFieldsRequest.extend({
  kind: MissionKindEditUserIcon,
  params: EditUserIconIncludePastParamsRequest.describe("パラメータ（なし）"),
  timeScope: MissionTimeScope.describe("ミッション達成対象判定の期間の範囲"),
})

const CreateSetSuperCallUserIncludePastMissionRequest = StampMissionBaseFieldsRequest.extend({
  kind: MissionKindSetSuperCallUser,
  params: SetSuperCallUserIncludePastParamsRequest.describe("パラメータ（なし）"),
  timeScope: MissionTimeScope.describe("ミッション達成対象判定の期間の範囲"),
})

const CreateFollowUsersIncludePastMissionRequest = StampMissionBaseFieldsRequest.extend({
  kind: MissionKindFollowUsers,
  params: FollowUsersIncludePastParamsRequest.describe("フォロー人数パラメータ"),
  timeScope: MissionTimeScope.describe("ミッション達成対象判定の期間の範囲"),
})

const CreateSnsLinkIncludePastMissionRequest = StampMissionBaseFieldsRequest.extend({
  kind: MissionKindSnsLink,
  params: SnsLinkIncludePastParamsRequest.describe("パラメータ（なし）"),
  timeScope: MissionTimeScope.describe("ミッション達成対象判定の期間の範囲"),
})

const CreateShareFriendLinkIncludePastMissionRequest = StampMissionBaseFieldsRequest.extend({
  kind: MissionKindShareFriendLink,
  params: ShareFriendLinkIncludePastParamsRequest.describe("パラメータ（なし）"),
  timeScope: MissionTimeScope.describe("ミッション達成対象判定の期間の範囲"),
})

const CreateTalkMinutesWithNUsersIncludePastMissionRequest = StampMissionBaseFieldsRequest.extend({
  kind: MissionKindTalkMinutesWithNUsers,
  params: TalkMinutesWithNUsersIncludePastParamsRequest.describe("ユーザー数と会話分数パラメータ"),
  timeScope: MissionTimeScopeSinceMissionActivated.describe("ミッション達成対象判定の期間の範囲（since_mission_activated のみ）"),
})

export const CreateStampMissionRequest = z.discriminatedUnion("kind", [
  // デイリーミッション
  CreateLoginInTimeRangeFromNowMissionRequest,
  CreateJoinSpaceFromNowMissionRequest,
  CreateWatchLiveMinutesFromNowMissionRequest,
  // 限定ミッション
  CreateLoginDaysFromNowMissionRequest,
  CreateEditUserIconIncludePastMissionRequest,
  CreateSetSuperCallUserIncludePastMissionRequest,
  CreateFollowUsersIncludePastMissionRequest,
  CreateSnsLinkIncludePastMissionRequest,
  CreateShareFriendLinkIncludePastMissionRequest,
  CreateTalkMinutesWithNUsersIncludePastMissionRequest,
])

export type CreateStampMissionRequest = z.infer<typeof CreateStampMissionRequest>

const UpdateStampMissionBaseFieldsRequest = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  awardStamps: z.number().optional(),
  priority: z.number().optional(),
  status: DocumentStatus.optional(),
})

// ========================================
// Update リクエスト定義
// ========================================

// デイリーミッション
const UpdateLoginInTimeRangeFromNowMissionRequest = z.object({
  kind: MissionKindLoginInTimeRange,
  params: LoginInTimeRangeFromNowParamsRequest.optional(),
}).extend(UpdateStampMissionBaseFieldsRequest.shape)

const UpdateJoinSpaceFromNowMissionRequest = z.object({
  kind: MissionKindJoinSpace,
  params: JoinSpaceFromNowParamsRequest.optional(),
}).extend(UpdateStampMissionBaseFieldsRequest.shape)

const UpdateWatchLiveMinutesFromNowMissionRequest = z.object({
  kind: MissionKindWatchLiveMinutes,
  params: WatchLiveMinutesFromNowParamsRequest.optional(),
}).extend(UpdateStampMissionBaseFieldsRequest.shape)

// 限定ミッション
const UpdateLoginDaysFromNowMissionRequest = z.object({
  kind: MissionKindLoginDays,
  params: LoginDaysFromNowParamsRequest.optional(),
  timeScope: MissionTimeScope.optional().describe("ミッション達成対象判定の期間の範囲"),
}).extend(UpdateStampMissionBaseFieldsRequest.shape)

const UpdateEditUserIconIncludePastMissionRequest = z.object({
  kind: MissionKindEditUserIcon,
  params: EditUserIconIncludePastParamsRequest.optional(),
  timeScope: MissionTimeScope.optional().describe("ミッション達成対象判定の期間の範囲"),
}).extend(UpdateStampMissionBaseFieldsRequest.shape)


const UpdateSetSuperCallUserIncludePastMissionRequest = z.object({
  kind: MissionKindSetSuperCallUser,
  params: SetSuperCallUserIncludePastParamsRequest.optional(),
  timeScope: MissionTimeScope.optional().describe("ミッション達成対象判定の期間の範囲"),
}).extend(UpdateStampMissionBaseFieldsRequest.shape)

const UpdateFollowUsersIncludePastMissionRequest = z.object({
  kind: MissionKindFollowUsers,
  params: FollowUsersIncludePastParamsRequest.optional(),
  timeScope: MissionTimeScope.optional().describe("ミッション達成対象判定の期間の範囲"),
}).extend(UpdateStampMissionBaseFieldsRequest.shape)

const UpdateSnsLinkIncludePastMissionRequest = z.object({
  kind: MissionKindSnsLink,
  params: SnsLinkIncludePastParamsRequest.optional(),
  timeScope: MissionTimeScope.optional().describe("ミッション達成対象判定の期間の範囲"),
}).extend(UpdateStampMissionBaseFieldsRequest.shape)

const UpdateShareFriendLinkIncludePastMissionRequest = z.object({
  kind: MissionKindShareFriendLink,
  params: ShareFriendLinkIncludePastParamsRequest.optional(),
  timeScope: MissionTimeScope.optional().describe("ミッション達成対象判定の期間の範囲"),
}).extend(UpdateStampMissionBaseFieldsRequest.shape)

const UpdateTalkMinutesWithNUsersIncludePastMissionRequest = z.object({
  kind: MissionKindTalkMinutesWithNUsers,
  params: TalkMinutesWithNUsersIncludePastParamsRequest.optional(),
  timeScope: MissionTimeScopeSinceMissionActivated.optional().describe("ミッション達成対象判定の期間の範囲（since_mission_activated のみ）"),
}).extend(UpdateStampMissionBaseFieldsRequest.shape)

const UpdateStampMissionRequestWhenKindPresent = z.discriminatedUnion("kind", [
  // デイリーミッション
  UpdateLoginInTimeRangeFromNowMissionRequest,
  UpdateJoinSpaceFromNowMissionRequest,
  UpdateWatchLiveMinutesFromNowMissionRequest,
  // 限定ミッション
  UpdateLoginDaysFromNowMissionRequest,
  UpdateEditUserIconIncludePastMissionRequest,
  UpdateSetSuperCallUserIncludePastMissionRequest,
  UpdateFollowUsersIncludePastMissionRequest,
  UpdateSnsLinkIncludePastMissionRequest,
  UpdateShareFriendLinkIncludePastMissionRequest,
  UpdateTalkMinutesWithNUsersIncludePastMissionRequest,
])

const UpdateStampMissionRequestWithoutParams = UpdateStampMissionBaseFieldsRequest.extend({
  kind: StampMissionKind.optional(),
  params: z.undefined().optional(),
})

export const UpdateStampMissionRequest = z.union([
  UpdateStampMissionRequestWhenKindPresent,
  UpdateStampMissionRequestWithoutParams,
])

export type UpdateStampMissionRequest = z.infer<typeof UpdateStampMissionRequest>

export const ForceUpdateStampMissionProgressRequest = z.object({
  completeStatus: z.enum(["in-progress", "force-completed"]).describe("変更後のミッション達成ステータス。強制変更では force-completed ↔ in-progress のみ許容"),
  dayKey: z.string().optional().describe("デイリーミッションの場合の対象日付キー（YYYY-MM-DD形式）。Limited ミッションの場合は不要"),
})

export type ForceUpdateStampMissionProgressRequest = z.infer<typeof ForceUpdateStampMissionProgressRequest>

export function getProgressKindFromMissionKind(kind: StampMissionKind): StampMissionProgressKind {
  const missionKind = allMissionKinds.find(m => m.value === kind)
  if (!missionKind) {
    throw new Error(`Unknown mission kind: ${kind}`)
  }
  return missionKind.progressKind
}

export function getRepeatKindFromMissionKind(kind: StampMissionKind): MissionRepeatKind {
  const missionKind = allMissionKinds.find(m => m.value === kind)
  if (!missionKind) {
    throw new Error(`Unknown mission kind: ${kind}`)
  }
  return missionKind.kind
}

/**
 * counterタイプのミッションから目標値を取得する
 * 新しいcounterミッションを追加した場合、ここにcaseを追加しないとTypeScriptエラーになる
 */
export function getTargetCountFromMission(mission: StampMissionDocument): number {
  switch (mission.kind) {
    case "watch_live_minutes":
      return mission.params.target_minutes
    case "login_days":
      return mission.params.target_days
    case "follow_users":
      return mission.params.target_users
    case "talk_minutes_with_n_users":
      return mission.params.target_minutes
    case "login_in_time_range":
    case "join_space":
    case "edit_user_icon":
    case "set_super_call_user":
    case "sns_link":
    case "share_friend_link":
      // booleanタイプのミッションは目標値がないため1を返す
      return 1
    default: {
      // 新しいミッションkindを追加した場合、ここでコンパイルエラーになる
      const _exhaustiveCheck: never = mission
      void _exhaustiveCheck
      throw new Error("Unknown mission kind")
    }
  }
}

export class StampMissionDocuments {

  static toDocumentData(doc: StampMissionDocumentBody): object {
    return {
      ...doc,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): StampMissionDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const kind = data["kind"]
    const repeat_kind = data["repeat_kind"] ?? getRepeatKindFromMissionKind(kind)
    const base = {
      id: StampMissionId(doc.id),
      repeat_kind,
      card_id: data["card_id"],
      name: data["name"],
      description: data["description"],
      award_stamps: data["award_stamps"],
      priority: data["priority"],
      status: data["status"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }

    // デイリーミッション
    if (kind === MissionKindLoginInTimeRange.value) {
      return {
        ...base,
        kind: MissionKindLoginInTimeRange.value,
        params: data["params"],
      }
    }
    if (kind === MissionKindJoinSpace.value) {
      return {
        ...base,
        kind: MissionKindJoinSpace.value,
        params: data["params"] ?? {},
      }
    }
    if (kind === MissionKindWatchLiveMinutes.value) {
      return {
        ...base,
        kind: MissionKindWatchLiveMinutes.value,
        params: data["params"],
      }
    }
    // 限定ミッション
    if (kind === MissionKindLoginDays.value) {
      return {
        ...base,
        kind: MissionKindLoginDays.value,
        params: data["params"],
        time_scope: data["time_scope"],
      }
    }
    if (kind === MissionKindEditUserIcon.value) {
      return {
        ...base,
        kind: MissionKindEditUserIcon.value,
        params: data["params"] ?? {},
        time_scope: data["time_scope"],
      }
    }
    if (kind === MissionKindSetSuperCallUser.value) {
      return {
        ...base,
        kind: MissionKindSetSuperCallUser.value,
        params: data["params"] ?? {},
        time_scope: data["time_scope"],
      }
    }
    if (kind === MissionKindFollowUsers.value) {
      return {
        ...base,
        kind: MissionKindFollowUsers.value,
        params: data["params"],
        time_scope: data["time_scope"],
      }
    }
    if (kind === MissionKindSnsLink.value) {
      return {
        ...base,
        kind: MissionKindSnsLink.value,
        params: data["params"] ?? {},
        time_scope: data["time_scope"],
      }
    }
    if (kind === MissionKindShareFriendLink.value) {
      return {
        ...base,
        kind: MissionKindShareFriendLink.value,
        params: data["params"] ?? {},
        time_scope: data["time_scope"],
      }
    }
    if (kind === MissionKindTalkMinutesWithNUsers.value) {
      return {
        ...base,
        kind: MissionKindTalkMinutesWithNUsers.value,
        params: data["params"],
        time_scope: data["time_scope"],
      }
    }
    return null
  }
}
